/**
 * Beschreibung der Felder je Stammdatenart.
 *
 * Die acht Arten unterscheiden sich nur in ihren Feldern. Statt acht fast
 * gleicher Masken zu schreiben, steht hier, welche Felder eine Art hat -- und
 * eine einzige Maske baut sich daraus auf.
 *
 * Der Gewinn ist nicht nur die Kuerze: Eine Aenderung an der Maske -- ein
 * Pflichtkennzeichen, eine Hilfezeile, ein anderes Bedienelement -- wirkt
 * damit ueberall gleich. Acht abgeschriebene Masken liefen auseinander,
 * sobald jemand eine davon anfasst.
 */

import type { MasterDataKind } from './api';

export type FieldKind = 'text' | 'number' | 'color' | 'boolean' | 'select' | 'reference';

/**
 * Woher ein Verweisfeld seine Auswahl holt.
 *
 * Ein ``select`` traegt seine Werte fest im Code; ein ``reference`` laedt sie
 * vom Server. Beides in einem Feldtyp zu fuehren hiesse, bei jedem Feld zu
 * pruefen, welche der beiden Herkuenfte gilt.
 */
export type ReferenceSource = 'user' | 'signature' | 'calendar';

export interface FieldSpec {
  /** Name im Rumpf der Anfrage -- muss zum Schema des Servers passen. */
  name: string;
  /** i18n-Schluessel der Beschriftung. */
  labelKey: string;
  kind: FieldKind;
  /** i18n-Schluessel eines Hinweises unter dem Feld. */
  hintKey?: string;
  required?: boolean;
  /** Nur bei ``select``: Wert und i18n-Schluessel je Option. */
  options?: { value: string; labelKey: string }[];
  /** Nur bei ``reference``: welche Liste vom Server geholt wird. */
  source?: ReferenceSource;
  /** Nur bei ``reference``: Beschriftung der leeren Auswahl. */
  emptyLabelKey?: string;
  /** Nur beim Anlegen zeigen -- der Server laesst die Angabe spaeter nicht mehr zu. */
  createOnly?: boolean;
  min?: number;
  max?: number;
}

const NAME: FieldSpec = {
  name: 'name',
  labelKey: 'masterdata.name',
  kind: 'text',
  required: true,
};

const SORT_ORDER: FieldSpec = {
  name: 'sort_order',
  labelKey: 'masterdata.sortOrder',
  kind: 'number',
  min: 0,
};

const IS_DEFAULT: FieldSpec = {
  name: 'is_default',
  labelKey: 'masterdata.isDefault',
  kind: 'boolean',
  hintKey: 'masterdata.isDefaultHint',
};

const IS_ACTIVE: FieldSpec = {
  name: 'is_active',
  labelKey: 'masterdata.isActive',
  kind: 'boolean',
  hintKey: 'masterdata.isActiveHint',
};

const LEVEL: FieldSpec = {
  name: 'level',
  labelKey: 'masterdata.level',
  kind: 'number',
  hintKey: 'masterdata.levelHint',
  required: true,
  min: 1,
  max: 5,
};

const STATE_CATEGORIES = [
  'new',
  'open',
  'pending_customer',
  'pending_internal',
  'resolved',
  'closed',
] as const;

const TYPE_KEYS = ['incident', 'service_request', 'problem', 'change'] as const;

export const FIELD_SPECS: Record<MasterDataKind, FieldSpec[]> = {
  queue: [
    NAME,
    {
      name: 'email_address',
      labelKey: 'masterdata.emailAddress',
      kind: 'text',
      hintKey: 'masterdata.emailAddressHint',
    },
    {
      name: 'default_owner_id',
      labelKey: 'masterdata.defaultOwner',
      kind: 'reference',
      source: 'user',
      emptyLabelKey: 'tickets.unassigned',
      hintKey: 'masterdata.defaultOwnerHint',
    },
    // Signatur und Kalender je Warteschlange -- F3-06, F1-01. Beide wahlfrei:
    // Der Service Desk und die Buchhaltung unterschreiben verschieden, eine
    // Bereitschaft rechnet anders als der Tagesdienst -- aber wer nichts
    // Eigenes braucht, soll nichts einstellen muessen.
    {
      name: 'signature_id',
      labelKey: 'masterdata.signature',
      kind: 'reference',
      source: 'signature',
      emptyLabelKey: 'masterdata.defaultSignature',
      hintKey: 'masterdata.signatureHint',
    },
    {
      name: 'business_calendar_id',
      labelKey: 'masterdata.businessCalendar',
      kind: 'reference',
      source: 'calendar',
      emptyLabelKey: 'masterdata.slaCalendar',
      hintKey: 'masterdata.businessCalendarHint',
    },
    IS_DEFAULT,
    IS_ACTIVE,
    SORT_ORDER,
  ],
  state: [
    NAME,
    {
      name: 'state_category',
      labelKey: 'masterdata.stateCategory',
      kind: 'select',
      hintKey: 'masterdata.stateCategoryHint',
      required: true,
      options: STATE_CATEGORIES.map((value) => ({
        value,
        labelKey: `stateCategory.${value}`,
      })),
    },
    { name: 'pauses_sla', labelKey: 'masterdata.pausesSla', kind: 'boolean' },
    IS_DEFAULT,
    SORT_ORDER,
  ],
  type: [
    NAME,
    {
      name: 'key',
      labelKey: 'masterdata.typeKey',
      kind: 'select',
      required: true,
      // Der Schluessel steuert spaetere Prozesse und laesst sich nicht
      // aendern -- deshalb nur beim Anlegen.
      createOnly: true,
      options: TYPE_KEYS.map((value) => ({ value, labelKey: `ticketType.${value}` })),
    },
    IS_DEFAULT,
    SORT_ORDER,
  ],
  priority: [
    NAME,
    LEVEL,
    { name: 'color', labelKey: 'masterdata.color', kind: 'color' },
    IS_DEFAULT,
    SORT_ORDER,
  ],
  impact: [NAME, LEVEL, IS_DEFAULT, SORT_ORDER],
  urgency: [NAME, LEVEL, IS_DEFAULT, SORT_ORDER],
  tag: [NAME, { name: 'color', labelKey: 'masterdata.color', kind: 'color' }, IS_ACTIVE],
  activity_type: [
    NAME,
    {
      name: 'billable_default',
      labelKey: 'time.billableDefault',
      kind: 'boolean',
      hintKey: 'time.billableDefaultHint',
    },
    IS_DEFAULT,
    IS_ACTIVE,
    SORT_ORDER,
  ],
};

/** Die Spalten, die in der Uebersicht erscheinen -- eine Auswahl der Felder. */
export const COLUMN_SPECS: Record<MasterDataKind, string[]> = {
  queue: ['name', 'email_address', 'is_default', 'is_active', 'sort_order'],
  state: ['name', 'state_category', 'pauses_sla', 'is_default', 'sort_order'],
  type: ['name', 'key', 'is_default', 'sort_order'],
  priority: ['name', 'level', 'color', 'is_default', 'sort_order'],
  impact: ['name', 'level', 'is_default', 'sort_order'],
  urgency: ['name', 'level', 'is_default', 'sort_order'],
  tag: ['name', 'color', 'is_active'],
  activity_type: ['name', 'billable_default', 'is_default', 'is_active', 'sort_order'],
};

export const MASTER_DATA_KINDS: MasterDataKind[] = [
  'queue',
  'state',
  'type',
  'priority',
  'impact',
  'urgency',
  'tag',
  'activity_type',
];

export function defaultValuesFor(specs: FieldSpec[]): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const spec of specs) {
    switch (spec.kind) {
      case 'boolean':
        values[spec.name] = spec.name === 'is_active';
        break;
      case 'number':
        values[spec.name] = spec.name === 'level' ? 3 : 0;
        break;
      case 'color':
        values[spec.name] = '#0070F2';
        break;
      case 'select':
        values[spec.name] = spec.options?.[0]?.value ?? '';
        break;
      default:
        values[spec.name] = '';
    }
  }
  return values;
}

/**
 * Formt einen Wert unbekannten Typs fuer ein Eingabefeld.
 *
 * ``String(unknown)`` waere hier falsch: Ein Objekt ergaebe "[object Object]"
 * und stuende dann als Muell im Feld. Was nicht darstellbar ist, wird zu einer
 * leeren Eingabe -- das faellt auf und richtet keinen Schaden an.
 */
export function toInputValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}
